// Localization setup
export type SupportedLang = 'en' | 'no' | 'da' | 'sv' | 'kl' | 'fo';

export const translations: Record<SupportedLang, Record<string, string>> = {
  en: {
    nowcast: 'Nowcast',
    map: 'Map',
    aurora: 'About aurora',
    settings: 'Settings',
    welcome: 'Welcome to Nordlys',
    track: 'Track aurora activity and get notifications when the northern lights are visible in your area.',
    subscribe: 'Subscribe to Notifications',
    unsubscribe: 'Unsubscribe from Notifications',
    notificationsBlocked: 'Notifications Blocked',
    enableNotifications: 'Enable Notifications',
    getNotified: 'Get notified when aurora activity is detected in your area',
    settingsTitle: 'Settings',
    language: 'Language',
    selectLanguage: 'Select Language',
    location: 'Location',
    notifications: 'Notifications',
    auroraTitle1: 'Quick guide',
    auroraText1: 'The best chance to see strong auroras is: <ul><li>When the sky is dark and free of clouds, avoid full moon and city light,</li><li>When you are close to the polar ovals,</li><li>The hours around midnight,</li><li>In autumn, winter, spring (but especially around the equinoxes),</li><li>During high geomagnetic activity,</li><li>In a period of high solar activity (the solar cycle is an 11-year cycle where the Sun is more or less active).</li></ul>',
    auroraTitle2: 'A global phenomenon',
    auroraText2: '<div>The northern lights are often described in literature, but did you know that there is a similar phenomenon in the southern hemisphere, called the southern lights?</div><div>The northern and southern lights, aurora borealis and aurora australis, appear almost simultaneously on each side of the Earth. The region where the aurora occurs is invisibly connected by the Earth\'s magnetic field lines. Northern and southern lights are usually visible in broad bands several hundred kilometres from the poles.</div>',
    auroraTitle3: 'The meeting',
    auroraText3: '<div>The story of northern and southern lights starts with the Sun. The Sun constantly ejects particles into space, called the solar wind. The solar wind, that also carries a magnetic field from the sun, is thrown out in all directions in space, thus also towards Earth.</div><div>The Earth also has a magnetic field, which usually acts as an invisible shield against the solar wind, therefore the solar wind passes around the shield and further out in space.</div><div>But sometimes, the magnetic field in the solar wind and the Earth\'s magnetic field are interconnected, which triggers complex processes that accelerate charged particles in the Earth\'s magnetosphere. The Earth\'s magnetosphere is the area of space encapsulated by the Earth\'s magnetic field. Some of these particles move along the magnetic field lines towards the Earth\'s atmosphere.</div>',
    auroraTitle4: 'Macro & micro cosmos',
    auroraText4: 'The particles from the Earth\'s magnetosphere follow the magnetic field lines down toward the polar regions, where they encounter molecules in the Earth\'s atmosphere. At this meeting, northern and southern lights are created. When the particles collide with nitrogen and oxygen molecules, green, red, blue, or violet light glows in the sky. When this happens to many particles at the same time, we can see the phenomenon with our eyes.',
    auroraTitle5: 'Always changing',
    auroraText5: 'Northern lights and southern lights are not just a green line in the sky, but rather a dynamic phenomenon that is always changing in form and intensity. Sometimes this is visible only like a faint grey form, but at other times the whole sky lights up by powerful dancing bands in several different colours. Processes in the magnetosphere and atmosphere control the shape and intensity of the aurora.',
    auroraTitle6: 'Unsolved questions',
    auroraText6: 'Aurora is a complex physical phenomenon. Although we can describe some of the physics behind the northern and southern lights, there is still much we do not know. For example: What processes are crucial to the shapes and structures of the aurora? Why are northern and southern lights significantly different at times? What causes the particles from the magnetosphere to accelerate to the energy they have when they hit the atmosphere? These are some of the questions that researchers are trying to find answers to.',
    auroraTitle7: 'Mythology',
    auroraText7: 'There are different stories and legends about the northern lights in the Arctic regions. The Vikings associated the flickering light in the sky with the armor of the Valkyries, archaeologists have found shaman drums among the Sami where symbols of northern lights were depicted. In the Greenlandic Inuit culture, the northern lights are described as dead souls, who play football with a walrus skull. If you whistled or clapped your hands during the northern lights, you could risk that the dead souls took you with them.',
    latestAuroraActivity: 'Latest Aurora Activity',
    auroraLevel: 'Aurora Level',
    detectedAt: 'Detected at',
    noRecentActivity: 'No Recent Activity',
    noActivityMessage: 'There is currently no aurora activity detected near your location. Notifications will appear here when activity is detected.',
    auroraChancesLow: 'There are some chances for northern lights near you.',
    auroraChancesMedium: 'There are good chances for northern lights near you.',
    auroraChancesHigh: 'There are excellent chances for northern lights near you!',
    auroraChancesVeryHigh: 'Northern lights are very likely visible near you right now!',
    auroraForecast: 'Aurora Forecast',
    activityDetails: 'Activity Details',
    activityDetectedNear: 'activity detected near',
    timeIndicator: 'at',
    levelQuiet: 'Quiet',
    levelSome: 'Some',
    levelHigh: 'High',
    levelVeryHigh: 'Very high',
    levelExtreme: 'Extreme',
    levelNoData: 'No data',
    locationPermission: 'Location Permission',
    accessGranted: 'Access granted',
    accessDenied: 'Access denied',
    notRequested: 'Not requested',
    revokeAccess: 'Revoke Access',
    grantAccess: 'Grant Access',
    pushNotifications: 'Push Notifications',
    enabledAndSubscribed: 'Enabled and subscribed',
    permissionGrantedNotSubscribed: 'Permission granted, not subscribed',
    permissionDenied: 'Permission denied',
    disableNotifications: 'Disable Notifications',
    notificationLevel: 'Notification Level',
    selectNotificationLevel: 'Select minimum aurora level for notifications',
    levelUpdated: 'Notification level updated',
    levelUpdateError: 'Could not update notification level. Please try again later.',
    notificationEnableError: 'Could not enable notifications. Please try again later.',
    cache: 'Cache',
    auroraDataCache: 'Aurora Data Cache',
    stations: 'Stations',
    cached: 'Cached',
    noCache: 'No cache',
    polarOval: 'Polar Oval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Polar Electro Jet',
    storageUsed: 'used',
    loading: 'Loading...',
    clearCache: 'Clear Cache',
    showOvationModel: 'Show Ovation model',
    hideOvationModel: 'Hide Ovation model',
    close: 'Close',
    installApp: 'Install App',
    installMessage: 'Add this app to your Home Screen for faster access and a better experience.',
    tapShare: 'Tap the Share button in Safari.',
    selectAddToHomeScreen: 'Select "Add to Home Screen".',
    locationAccessTitle: 'Location Access',
    locationAccessMessage: 'Allow access to your location to see your position on the globe and get personalized aurora alerts.',
    allowLocation: 'Allow Location',
    notificationsTitle: 'Notifications',
    notificationsMessage: 'Get notified when aurora activity is detected in your area so you never miss the northern lights.',
    enableNotificationsAction: 'Enable Notifications',
    installOnIOS: 'Install on iOS',
    installIOSMessage: 'Tap Share in Safari, then "Add to Home Screen" to install this app.',
    subscriptionStatus: 'Subscription Status',
    nearestStation: 'Nearest station',
    stationDistance: 'Distance',
    withinRange: 'Within notification range',
    outsideRange: 'Outside notification range',
    activeSubscription: 'Active push notifications',
    stationSelection: 'Station Selection',
    automaticMode: 'Automatic (closest)',
    manualMode: 'Manual Selection',
    automaticModeDescription: 'Automatically subscribe to the closest station based on your location',
    loadingStations: 'Loading stations...',
    stationUpdateError: 'Failed to update station selection. Please try again.',
    subscribedToStations: 'Subscribed to stations:',
    station: 'station',
    noStationsSelected: 'No stations selected',
    stationActivityLevel: 'Station activity level',
    showAuroraOvals: 'Show aurora ovals',
    about: 'About',
    locationNotAvailable: 'Location not available',
    changeBrowserSettings: 'Change in browser settings',
    automaticModeRequiresLocation: 'Automatic mode requires location access to determine nearest stations',
  },
  no: {
    nowcast: 'Nowcast',
    map: 'Kart',
    aurora: 'Om nordlys',
    settings: 'Innstillinger',
    welcome: 'Velkommen til Nordlys',
    track: 'Følg med på nordlysaktivitet og få varsler når nordlyset er synlig i ditt område.',
    subscribe: 'Abonner på varsler',
    unsubscribe: 'Stopp varsler',
    notificationsBlocked: 'Varsler blokkert',
    enableNotifications: 'Aktiver varsler',
    getNotified: 'Få beskjed når nordlysaktivitet oppdages i ditt område',
    settingsTitle: 'Innstillinger',
    language: 'Språk',
    selectLanguage: 'Velg språk',
    location: 'Plassering',
    notifications: 'Varsler',
    auroraTitle1: 'Hurtigguide',
    auroraText1: 'Det er størst sjanse for å se sterkt nordlys: <ul><li>Når himmelen er mørk og skyfri, unngå helst fullmåne og lysforurensning,</li><li>Når du er nær nordlyset eller sørlysovalen (se en modellert plassering på kartet),</li><li>Fra kveld til morgen, men spesielt timene rundt midnatt,</li><li>Høst, vinter og vår (men spesielt rundt jevndøgn),</li><li>Under høy magnetisk aktivitet</li><li>Nær solsyklusens maksimum (solsyklusen er en 11-års syklus der solen er mer eller mindre aktiv).</li></ul>',
    auroraTitle2: 'Globalt fenomen',
    auroraText2: '<div>Vi kjenner til nordlyset, men visste du at det på den sørlige halvkule finnes et lignende lys som kalles sørlys?</div><div>Nordlyset og sørlyset vises nesten samtidig på motsatte sider av jorden. Området der lysene oppstår er usynlig forbundet via jordens magnetfeltlinjer. Nordlyset og sørlyset er oftest synlige i et bredt bånd flere hundre kilometer fra polene.</div>',
    auroraTitle3: 'Møtet',
    auroraText3: '<div>Møtet mellom universet og jorden. Historien om nordlyset begynner med solen. Solen sender stadig ut partikler som beveger seg gjennom rommet, dette kalles solvinden. Sammen med partiklene i solvinden følger et magnetfelt. På solvindens bane gjennom universet vil den også møte jorden.</div><div>Jorden har også et magnetfelt som ofte fungerer som et usynlig skjold mot solvinden, og det er derfor den vil passere rundt jorden og videre inn i universet.</div><div>Men noen ganger vil solvindens magnetfelt koble seg til jordens magnetfelt, og dermed starte komplekse prosesser som akselererer ladede partikler i jordens magnetosfære. Jordens magnetosfære er området som er innkapslet av jordens magnetfelt. Noen av disse partiklene beveger seg langs magnetfeltlinjer inn i jordens atmosfære.</div>',
    auroraTitle4: 'Makro- og mikrokosmos',
    auroraText4: 'Partikler fra jordens magnetosfære følger magnetfeltet ned mot polarområdene, hvor de møter molekyler fra jordens atmosfære. Dette møtet skaper nordlyset. Når partiklene kolliderer med nitrogen- og oksygenmolekyler, oppstår det grønne, røde, blå eller fiolette lysglimt. Når dette skjer med mange partikler, ser vi det som nordlyset på himmelen.',
    auroraTitle5: 'Alltid i endring',
    auroraText5: 'Polarlys er ikke bare en grønn linje på himmelen, men et dynamisk fenomen som alltid er i endring. Noen ganger vil vi bare se en svak gråaktig form, og andre ganger vil hele himmelen være opplyst av kraftige dansende bånd i flere farger. Det finnes flere forskjellige typer polarlys, avhengig av hva som skjer i magnetosfæren og atmosfæren.',
    auroraTitle6: 'Uløste gåter',
    auroraText6: 'Polarlys er et komplekst fysisk fenomen. Selv om vi kan beskrive deler av dannelsen av nordlys og sørlys, er mye fortsatt ukjent, for eksempel: Hvilke prosesser/mekanismer er avgjørende for formene og strukturene til nordlyset? Hvorfor er nordlys og sørlys noen ganger betydelig forskjellige fra hverandre? Hva akselererer partiklene fra magnetosfæren til atmosfæren? Dette er noen av spørsmålene forskere prøver å finne svar på.',
    auroraTitle7: 'Mytologi',
    auroraText7: 'I de arktiske områdene finnes det ulike beretninger om hva nordlyset kan være. Vikingene assosierte det flimrende lyset på himmelen med valkyrienes rustning, og blant samene har arkeologer funnet sjamantrommer der symboler for nordlyset er avbildet. I den grønlandske inuittkulturen beskrives nordlyset som avdøde sjeler som spiller ball med en hvalrossskalle. Hvis du plystret eller klappet i hendene mens nordlyset var på, kunne du risikere at de døde sjelene tok deg med deg.',
    latestAuroraActivity: 'Siste nordlysaktivitet',
    auroraLevel: 'Nordlysnivå',
    detectedAt: 'Oppdaget',
    noRecentActivity: 'Ingen nylig aktivitet',
    noActivityMessage: 'Det er for øyeblikket ingen nordlysaktivitet oppdaget nær din lokasjon. Varsler vil vises her når aktivitet blir oppdaget.',
    auroraChancesLow: 'Det er noen muligheter for nordlys nær deg.',
    auroraChancesMedium: 'Det er gode muligheter for nordlys nær deg.',
    auroraChancesHigh: 'Det er utmerkede muligheter for nordlys nær deg!',
    auroraChancesVeryHigh: 'Nordlys er meget sannsynlig synlig nær deg akkurat nå!',
    auroraForecast: 'Nordlysprognose',
    activityDetails: 'Aktivitetsdetaljer',
    activityDetectedNear: 'aktivitet nær',
    timeIndicator: 'kl.',
    levelQuiet: 'Stille',
    levelSome: 'Noe',
    levelHigh: 'Høy',
    levelVeryHigh: 'Meget høy',
    levelExtreme: 'Ekstrem',
    levelNoData: 'Ingen data',
    locationPermission: 'Plasseringstillatelse',
    accessGranted: 'Tilgang gitt',
    accessDenied: 'Tilgang nektet',
    notRequested: 'Ikke forespurt',
    revokeAccess: 'Tilbakekall tilgang',
    grantAccess: 'Gi tilgang',
    pushNotifications: 'Push-varsler',
    enabledAndSubscribed: 'Aktivert og abonnert',
    permissionGrantedNotSubscribed: 'Tillatelse gitt, ikke abonnert',
    permissionDenied: 'Tillatelse nektet',
    disableNotifications: 'Deaktiver varsler',
    notificationLevel: 'Varslingsnivå',
    selectNotificationLevel: 'Velg minimumsnivå for nordlysvarsler',
    levelUpdated: 'Varslingsnivå oppdatert',
    levelUpdateError: 'Kunne ikke oppdatere varslingsnivå. Prøv igjen senere.',
    notificationEnableError: 'Kunne ikke aktivere varsler. Prøv igjen senere.',
    cache: 'Cache',
    auroraDataCache: 'Nordlysdata-cache',
    stations: 'Stasjoner',
    cached: 'Cachet',
    noCache: 'Ingen cache',
    polarOval: 'Polaroval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Polar elektrojet',
    storageUsed: 'brukt',
    loading: 'Laster...',
    clearCache: 'Tøm cache',
    showOvationModel: 'Vis Ovation-modell',
    hideOvationModel: 'Skjul Ovation-modell',
    close: 'Lukk',
    installApp: 'Installer app',
    installMessage: 'Legg til denne appen på hjemskjermen for raskere tilgang og bedre opplevelse.',
    tapShare: 'Trykk på Del-knappen i Safari.',
    selectAddToHomeScreen: 'Velg "Legg til på hjemskjerm".',
    locationAccessTitle: 'Plasseringstilgang',
    locationAccessMessage: 'Tillat tilgang til din posisjon for å se hvor du er på globusen og få personaliserte nordlysvarsler.',
    allowLocation: 'Tillat posisjon',
    notificationsTitle: 'Varsler',
    notificationsMessage: 'Få beskjed når nordlysaktivitet blir oppdaget i ditt område så du aldri går glipp av nordlyset.',
    enableNotificationsAction: 'Aktiver varsler',
    installOnIOS: 'Installer på iOS',
    installIOSMessage: 'Trykk Del i Safari, deretter "Legg til på hjemskjerm" for å installere denne appen.',
    subscriptionStatus: 'Abonnementsstatus',
    nearestStation: 'Nærmeste stasjon',
    stationDistance: 'Avstand',
    withinRange: 'Innenfor varslingsområde',
    outsideRange: 'Utenfor varslingsområde',
    activeSubscription: 'Aktive push-varsler',
    stationSelection: 'Stasjonsvalg',
    automaticMode: 'Automatisk (nærmest)',
    manualMode: 'Manuell valg',
    automaticModeDescription: 'Abonner automatisk på den nærmeste stasjonen basert på din posisjon',
    loadingStations: 'Laster stasjoner...',
    stationUpdateError: 'Kunne ikke oppdatere stasjonsvalg. Prøv igjen.',
    subscribedToStations: 'Abonnerer på stasjoner:',
    station: 'stasjon',
    noStationsSelected: 'Ingen stasjoner valgt',
    stationActivityLevel: 'Stasjon aktivitetsnivå',
    showAuroraOvals: 'Vis nordlysovaler',
    about: 'Om',
    locationNotAvailable: 'Posisjon ikke tilgjengelig',
    changeBrowserSettings: 'Endre i nettleserinnstillinger',
    automaticModeRequiresLocation: 'Automatisk modus krever posisjonstilgang for å finne nærmeste stasjoner',
  },
  da: {
    nowcast: 'Nowcast',
    map: 'Kort',
    aurora: 'Om nordlys',
    settings: 'Indstillinger',
    welcome: 'Velkommen til Nordlys',
    track: 'Følg nordlysaktivitet og få besked, når nordlyset er synligt i dit område.',
    subscribe: 'Abonner på notifikationer',
    unsubscribe: 'Afmeld notifikationer',
    notificationsBlocked: 'Notifikationer blokeret',
    enableNotifications: 'Aktivér notifikationer',
    getNotified: 'Få besked, når nordlysaktivitet opdages i dit område',
    settingsTitle: 'Indstillinger',
    language: 'Sprog',
    selectLanguage: 'Vælg sprog',
    location: 'Lokalitet',
    notifications: 'Notifikationer',
    auroraTitle1: 'Hurtig guide',
    auroraText1: 'Der er størst chance for at se stærke polarlys: <ul><li>Når himlen er mørk og skyfri, undgå helst fuldmåne og lysforurening,</li><li>Når man er tæt på nordlys- eller sydlys ovalen (se en modelleret placering på kortet),</li><li> Fra aften til morgen, men specielt timerne omkring midnat,</li><li>Efterår, vinter og forår (men specielt omkring jævndøgn),</li><li>Ved høj magnetisk aktivitet</li><li>Tæt på solcykel maksimum (solcyklussen er en 11 år cyklus hvor Solen er mere eller mindre aktiv)</li></ul>',
    auroraTitle2: 'Globalt fænomen',
    auroraText2: '<div>Vi kender nordlys, men vidste du at på den sydlige halvkugle findes et lys magen til som kaldes sydlys?</div><div>Nordlys og sydlys optræder næsten samtidig på hver sin side af Jorden. Området hvor lysene opstår, er usynligt forbundet via Jordens magnetfeltslinjer. Nordlys og sydlys er oftest synlig i et bredt bånd flere hundrede kilometer fra polerne.</div>',
    auroraTitle3: 'Mødet',
    auroraText3: '<div>Mødet mellem Universet og Jorden. Fortællingen om nordlys begynder med Solen. Solen udsender konstant partikler som bevæger sig gennem verdensrummet, dette kaldes for solvinden. Sammen med partiklerne i solvinden følger et magnetfelt. På solvindens vej gennem universet vil den også møde Jorden.</div><div>Jorden har også et magnetfelt som oftest virker som et usynligt skjold mod solvinden, derfor vil den passere udenom Jorden og videre ud i universet.</div><div>Men nogle gange vil solvindens magnetfelt koble sig sammen med Jordens magnetfelt og dermed starte komplekse processer der accelererer ladede partikler i Jordens magnetosfære. Jordens magnetosfære er området som er indkapslet af Jordens magnetfelt. Nogle af disse partikler bevæger sig langs magnefeltlinjer ind i Jordens atmosfære.</div>',
    auroraTitle4: 'Makro & mikrokosmos',
    auroraText4: 'Partiklerne fra Jordens magnetosfære følger magnetfeltet ned mod de polare områder, hvor de møder molekyler fra Jordens atmosfære. Ved dette møde dannes nordlys. Når partiklerne kolliderer med kvælstof og ilt molekyler vil grønne, røde, blå eller violette lysglimt opstå. Når dette sker med mange partikler så ser vi det som nordlys på himlen.',
    auroraTitle5: 'Altid i forandring',
    auroraText5: 'Polarlys er ikke bare en grøn linje på himlen, men et dynamisk fænomen som altid er i forandring. Nogle gange vil vi kun se en svag grålig facon, og andre gange vil hele himlen være oplyst af kraftige dansende bånd i flere farver. Der findes nemlig flere forskellige slags polarlys afhængig af hvad der sker i magnetosfæren og atmosfæren.',
    auroraTitle6: 'Uløste gåder',
    auroraText6: 'Polarlys er et kompleks fysisk fænomen. Selvom vi kan beskrive en del af dannelsen af nordlys og sydlys ligger meget stadig hen i det uvisse, for eksempel: Hvilke processer/mekanismer er afgørende for polarlysets former og strukturer? Hvorfor er nordlys og sydlys nogle gange væsentlig forskellige fra hinanden? Hvad accelererer partiklerne fra magnetosfæren til atmosfæren? Dette er nogle af de spørgsmål som forskere prøver at finde svar på.',
    auroraTitle7: 'Mytologien',
    auroraText7: 'I de arktiske egne optræder forskellige beretninger om, hvad nordlyset mon var. Vikingerne forbandt det flakkende lys på himlen med valkyriernes rustninger, og hos samerne har arkæologer fundet shamantrummer, hvorpå symboler for nordlyset er afbildet. I den grønlandske Inuit-kultur beskrives nordlyset som afdøde sjæle, der spiller bold med et hvalroskranium. Hvis man fløjtede eller klappede i hænderne, mens nordlyset stod på, kunne man risikere, at de døde sjæle tog en med.',
    latestAuroraActivity: 'Seneste nordlysaktivitet',
    auroraLevel: 'Nordlysniveau',
    detectedAt: 'Opdaget',
    noRecentActivity: 'Ingen aktivitet',
    noActivityMessage: 'Der er i øjeblikket ingen nordlysaktivitet nær din lokation. Notifikationer vil blive vist her, når aktivitet opdages.',
    auroraChancesLow: 'Der er nogle muligheder for nordlys nær dig. Hold øje med himlen.',
    auroraChancesMedium: 'Der er gode muligheder for nordlys nær dig. Hold øje med himlen!',
    auroraChancesHigh: 'Der er fremragende muligheder for nordlys nær dig!',
    auroraChancesVeryHigh: 'Nordlys er meget sandsynligt synligt nær dig lige nu!',
    auroraForecast: 'Nordlysprognose',
    activityDetails: 'Aktivitetsdetaljer',
    activityDetectedNear: 'aktivitet nær',
    timeIndicator: 'kl.',
    levelQuiet: 'Stille',
    levelSome: 'Nogen',
    levelHigh: 'Høj',
    levelVeryHigh: 'Meget høj',
    levelExtreme: 'Ekstrem',
    levelNoData: 'Ingen data',
    locationPermission: 'Lokationstilladelse',
    accessGranted: 'Adgang givet',
    accessDenied: 'Adgang nægtet',
    notRequested: 'Ikke anmodet',
    revokeAccess: 'Tilbagekald adgang',
    grantAccess: 'Giv adgang',
    pushNotifications: 'Push-notifikationer',
    enabledAndSubscribed: 'Aktiveret og tilmeldt',
    permissionGrantedNotSubscribed: 'Tilladelse givet, ikke tilmeldt',
    permissionDenied: 'Tilladelse nægtet',
    disableNotifications: 'Deaktiver notifikationer',
    notificationLevel: 'Notifikationsniveau',
    selectNotificationLevel: 'Vælg minimumsniveau for nordlysnotifikationer',
    levelUpdated: 'Notifikationsniveau opdateret',
    levelUpdateError: 'Kunne ikke opdatere notifikationsniveau. Prøv igen senere.',
    notificationEnableError: 'Kunne ikke aktivere notifikationer. Prøv igen senere.',
    cache: 'Cache',
    auroraDataCache: 'Nordlysdata-cache',
    stations: 'Stationer',
    cached: 'Cachet',
    noCache: 'Ingen cache',
    polarOval: 'Polaroval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Polar elektrojet',
    storageUsed: 'brugt',
    loading: 'Indlæser...',
    clearCache: 'Ryd cache',
    showOvationModel: 'Vis Ovation-model',
    hideOvationModel: 'Skjul Ovation-model',
    close: 'Luk',
    installApp: 'Installer app',
    installMessage: 'Tilføj denne app til din hjemmeskærm for hurtigere adgang og bedre oplevelse.',
    tapShare: 'Tryk på Del-knappen i Safari.',
    selectAddToHomeScreen: 'Vælg "Føj til hjemmeskærm".',
    locationAccessTitle: 'Lokalitetsadgang',
    locationAccessMessage: 'Tillad adgang til din lokalitet for at se din position på kloden og få personlige nordlysvarsler.',
    allowLocation: 'Tillad lokalitet',
    notificationsTitle: 'Notifikationer',
    notificationsMessage: 'Få besked når nordlysaktivitet opdages i dit område så du aldrig går glip af nordlyset.',
    enableNotificationsAction: 'Aktivér notifikationer',
    installOnIOS: 'Installer på iOS',
    installIOSMessage: 'Tryk Del i Safari, derefter "Føj til hjemmeskærm" for at installere denne app.',
    subscriptionStatus: 'Abonnementsstatus',
    nearestStation: 'Nærmeste station',
    stationDistance: 'Afstand',
    withinRange: 'Inden for varslingsområde',
    outsideRange: 'Uden for varslingsområde',
    activeSubscription: 'Aktive push-notifikationer',
    stationSelection: 'Stationsvalg',
    automaticMode: 'Automatisk (nærmeste)',
    manualMode: 'Manuelt valg',
    automaticModeDescription: 'Abonnér automatisk på den nærmeste station baseret på din position',
    loadingStations: 'Indlæser stationer...',
    stationUpdateError: 'Kunne ikke opdatere stationsvalg. Prøv igen.',
    subscribedToStations: 'Abonnerer på stationer:',
    station: 'station',
    noStationsSelected: 'Ingen stationer valgt',
    stationActivityLevel: 'Aktivitetsniveau for stationer',
    showAuroraOvals: 'Vis nordlysovaler',
    about: 'Om',
    locationNotAvailable: 'Lokalitet ikke tilgængelig',
    changeBrowserSettings: 'Skift i browserindstillinger',
    automaticModeRequiresLocation: 'Automatisk tilstand kræver lokalitetsadgang for at finde nærmeste stationer',
  },
  sv: {
    nowcast: 'Nowcast',
    map: 'Karta',
    aurora: 'Om norrsken',
    settings: 'Inställningar',
    welcome: 'Välkommen till Nordlys',
    track: 'Följ norrskensaktivitet och få aviseringar när norrskenet är synligt i ditt område.',
    subscribe: 'Prenumerera på aviseringar',
    unsubscribe: 'Avsluta aviseringar',
    notificationsBlocked: 'Aviseringar blockerade',
    enableNotifications: 'Aktivera aviseringar',
    getNotified: 'Få avisering när norrskensaktivitet upptäcks i ditt område',
    settingsTitle: 'Inställningar',
    language: 'Språk',
    selectLanguage: 'Välj språk',
    location: 'Plats',
    notifications: 'Aviseringar',
    auroraTitle1: 'Snabb guide',
    auroraText1: 'Det är störst chans att se starka polarsken: <ul><li>När himlen är mörk och molnfri, undgå helst fullmåne och ljusföroreningar,</li><li>Då man är nära norrskens- eller sydskens ovalen,</li><li>Timmarna omkring midnatt,</li><li>Höst, vinter och vår (men speciellt omkring dagjämning),</li><li>Vid hög geomagnetisk aktivitet</li><li>Omkring solcykel maximum (solcyklen är en 11-årscykel där solen är mer eller mindre aktiv).</li></ul>',
    auroraTitle2: 'Globalt fenomen',
    auroraText2: '<div>De flesta av oss känner till norrsken, men vet du att det finns ett liknande fenomen på det södra halvklotet som kallas sydsken?</div><div>Norrsken och sydsken, aurora borealis och aurora australis, uppträder nästan samtidigt på var sin sida av jorden. Området där skenen uppstår, är osynligt förbundet av jordens magnetfältlinjer. Norrsken och sydsken är oftast synliga i breda band flera hundra kilometer från polerna.</div>',
    auroraTitle3: 'Mötet',
    auroraText3: '<div>Mötet mellan Universum och jorden: Berättelsen om norrsken och sydsken startar med solen. Solen slungar konstant ut partiklar som färdas genom världsrymden, detta kallas för solvinden. Solvinden för även med sig ett magnetfält från solen. Solvinden slungas ut i alla riktningar, därmed också mot jorden.</div><div>Jorden har också ett magnetfält, som oftast fungerar som en osynlig sköld mot solvinden, därför passerar solvinden förbi jorden och vidare ut i universum.</div><div>Men ibland, kopplas solvinden och jordens magnetfält ihop vilket startar komplexa processer som accelererar laddade partiklar i magnetosfären. Jordens magnetosfär är det område i rymden som är inkapslat av jordens magnetfält. Några av dessa partiklar rör sig längst magnetfältlinjerna in mot jordens atmosfär.</div>',
    auroraTitle4: 'Makro- & mikrokosmos',
    auroraText4: 'Partiklarna från jordens magnetosfär följer magnetfältet ned mot de polära områden, där de möter molekyler i jordens atmosfär. Vid detta möte skapas norrsken och sydsken. När partiklarna kolliderar med kväve- och syrenmolekyler uppstår gröna, röda, blå eller violetta ljusglimtar på himlen. När detta händer med många partiklar samtidigt kan vi se fenomenet med våra egna ögon.',
    auroraTitle5: 'Alltid i förändring',
    auroraText5: 'Norrsken och sydsken är inte bara en grön linje på himlen, det är ett dynamiskt fenomen som alltid är under förändring. Ibland ses bara en svag grå yta, andra gånger skiner hela himlen upp av kraftiga dansande band i flera färger. Processer i magnetosfären och atmosfären styr vad för slags aurora som uppträder.',
    auroraTitle6: 'Olösta gåtor',
    auroraText6: 'Aurora är ett komplext fysiskt fenomen. Trots att vi idag kan beskriva en del av uppkomsten av norr- och sydsken, är det fortfarande mycket vi inte vet. Till exempel: Vilka processer är avgörande för norr- och sydskenets former och strukturer? Varför är norr- och sydsken betydligt olika ibland? Vad får partiklarna från magnetosfären att accelerera till den energi de har när de träffar atmosfären? Detta är några av de frågor som forskare försöker finna svar på.',
    auroraTitle7: 'Mytologin',
    auroraText7: 'I de arktiska områden uppträder olika berättelser om vad norrsken är för något. Vikingarna förknippade det flammande ljuset på himlen med Valkyriernas rustningar, hos samerna har arkeologer funnit shamantrummor, där symboler av norrsken var avbildat. I den Grönländska inuit-kultur beskrivs norrskenet som döda själar, som spelar fotboll med ett valrosskranium. Om man visslande eller klappade i händerna under norrskenet, kunde man riskera att de döda själarna tog en med.',
    latestAuroraActivity: 'Senaste norrskensaktivitet',
    auroraLevel: 'Norrskensnivå',
    detectedAt: 'Upptäckt',
    noRecentActivity: 'Ingen nylig aktivitet',
    noActivityMessage: 'Det finns för närvarande ingen norrskensaktivitet upptäckt på din plats. Aviseringar kommer att visas här när aktivitet upptäcks.',
    auroraChancesLow: 'Det finns några möjligheter för norrsken nära dig.',
    auroraChancesMedium: 'Det finns goda möjligheter för norrsken nära dig.',
    auroraChancesHigh: 'Det finns utmärkta möjligheter för norrsken nära dig!',
    auroraChancesVeryHigh: 'Norrsken är mycket troligt synligt nära dig just nu!',
    auroraForecast: 'Norrskensprognos',
    activityDetails: 'Aktivitetsdetaljer',
    activityDetectedNear: 'aktivitet nära',
    timeIndicator: 'kl.',
    levelQuiet: 'Låg',
    levelSome: 'Någon',
    levelHigh: 'Hög',
    levelVeryHigh: 'Mycket hög',
    levelExtreme: 'Extrem',
    levelNoData: 'Ingen data',
    locationPermission: 'Platstillstånd',
    accessGranted: 'Åtkomst beviljad',
    accessDenied: 'Åtkomst nekad',
    notRequested: 'Inte begärd',
    revokeAccess: 'Återkalla åtkomst',
    grantAccess: 'Bevilja åtkomst',
    pushNotifications: 'Push-aviseringar',
    enabledAndSubscribed: 'Aktiverad och prenumererad',
    permissionGrantedNotSubscribed: 'Tillstånd beviljat, inte prenumererad',
    permissionDenied: 'Tillstånd nekat',
    disableNotifications: 'Inaktivera aviseringar',
    notificationLevel: 'Aviseringsnivå',
    selectNotificationLevel: 'Välj minimumnivå för norrskenaviseringar',
    levelUpdated: 'Aviseringsnivå uppdaterad',
    levelUpdateError: 'Kunde inte uppdatera aviseringsnivå. Försök igen senare.',
    notificationEnableError: 'Kunde inte aktivera aviseringar. Försök igen senare.',
    cache: 'Cache',
    auroraDataCache: 'Norrskendata-cache',
    stations: 'Stationer',
    cached: 'Cachad',
    noCache: 'Ingen cache',
    polarOval: 'Polaroval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Polar elektrojet',
    storageUsed: 'använt',
    loading: 'Laddar...',
    clearCache: 'Rensa cache',
    showOvationModel: 'Visa Ovation-modell',
    hideOvationModel: 'Dölj Ovation-modell',
    close: 'Stäng',
    installApp: 'Installera app',
    installMessage: 'Lägg till denna app på din hemskärm för snabbare åtkomst och bättre upplevelse.',
    tapShare: 'Tryck på Dela-knappen i Safari.',
    selectAddToHomeScreen: 'Välj "Lägg till på hemskärmen".',
    locationAccessTitle: 'Platsåtkomst',
    locationAccessMessage: 'Tillåt åtkomst till din plats för att se din position på klotet och få personliga norrskensaviseringar.',
    allowLocation: 'Tillåt plats',
    notificationsTitle: 'Aviseringar',
    notificationsMessage: 'Få avisering när norrskensaktivitet upptäcks i ditt område så du aldrig missar norrskenet.',
    enableNotificationsAction: 'Aktivera aviseringar',
    installOnIOS: 'Installera på iOS',
    installIOSMessage: 'Tryck Dela i Safari, sedan "Lägg till på hemskärmen" för att installera denna app.',
    subscriptionStatus: 'Prenumerationsstatus',
    nearestStation: 'Närmaste station',
    stationDistance: 'Avstånd',
    withinRange: 'Inom aviseringsområde',
    outsideRange: 'Utanför aviseringsområde',
    activeSubscription: 'Aktiva push-aviseringar',
    stationSelection: 'Stationsval',
    automaticMode: 'Automatisk (närmaste)',
    manualMode: 'Manuellt val',
    automaticModeDescription: 'Prenumerera automatiskt på den närmaste stationen baserat på din position',
    loadingStations: 'Laddar stationer...',
    stationUpdateError: 'Kunde inte uppdatera stationsval. Försök igen.',
    subscribedToStations: 'Prenumererar på stationer:',
    station: 'station',
    noStationsSelected: 'Inga stationer valda',
    stationActivityLevel: 'Stationsaktivitetsnivå',
    showAuroraOvals: 'Visa norrskensovalare',
    about: 'Om',
    locationNotAvailable: 'Plats inte tillgänglig',
    changeBrowserSettings: 'Ändra i webbläsarinställningar',
    automaticModeRequiresLocation: 'Automatiskt läge kräver platsåtkomst för att hitta närmaste stationer',
  },
  kl: {
    nowcast: 'Nowcast',
    map: 'Assiliartaq',
    aurora: 'Aurora',
    settings: 'Aaqqissuutissat',
    welcome: 'Tikilluarit Nordlys-imut',
    track: 'Aurora pillugu paasissutissat malinnaavigikkit aammalu qaammarsagaaneq sumiiffinni takuneqarsinnaappat nalunaarutigineqassaaq.',
    subscribe: 'Nalunaarutigineqarnissamut allatsigit',
    unsubscribe: 'Nalunaarutigineqarnissamut allagartinneqanngilaq',
    notificationsBlocked: 'Nalunaarutigineqarnissamut mattunneqartoq',
    enableNotifications: 'Nalunaarutigineqarnissamut atuutilersitsineq',
    getNotified: 'Aurora pillugu paasissutissat sumiiffinni paasineqarpat nalunaarutigineqassaaq',
    settingsTitle: 'Aaqqissuutissat',
    language: 'Oqaatsit',
    selectLanguage: 'Oqaatsit toqqakkit',
    location: 'Sumiiffia',
    notifications: 'Nalunaarutigineqartut',
    auroraTitle1: 'Najoqqutassiaq',
    auroraText1: 'Imaaligaangat arsarnernik takunissaq ilimanarnerusarpoq: <ul><li>Qilak taaraangat nuiaqaranilu. Qaammat imissisimavippat qulleqarpallu ajoqutaasinnaapput,</li><li>Arsarneqarfiusartumut tumaasatut ilusilimmut qanikkaanni (titartagaq takuuk sumiiffialu paasillugu),</li><li>Unnulernerani ullaanngornissaata tungaanut, pingaartumik unnuap-qeqqata missaani,</li><li>Ukiakkut, ukiukkut upernaakkullu, pingaartumik ukialeqqaarfimmi / upernaleqqaarfimmi,</li><li>Magnetiskimik pisoqarluaraangat (Aaqqissuiffik aqqutigalugu nalunaarfigitittarnissannut periarfissaqarputit),</li><li>Seqinermi pisoqarnerpaaffimmi (Seqineq cykluseqarpoq ukuini 11-ni ingerlaartumi, ukiunilu taakkunani annertunerusumik annikinnerusumilluunniit pisoqartarpoq.).</li></ul>',
    auroraTitle2: 'Nunarsuaq tamakkerlugu malunnaatilik',
    auroraText2: '<div>Arsarnerit nunarsuup affaata avannarlertaaniittartut pillugit atuakkani allaaserineqallattaasarput, kisianni ilisimaviuk nunarsuup affaata kujallertaani aamma arsarneqartartoq?</div><div>Arsarnerit avannarlermi kujallermilu ataatsikkuungajallutik nuisasarput. Piffik arsarnerit aallartiffiat, Nunarsuup magneteqarfianut ersinngitsumik atassuteqarpoq. Arsarnerit nunarsuup kaaviffiata isuiniit 2000 kilometerit sinnerlugit ungasissusilimmiit titarnertut issusuutut isikkoqarlutik takuneqarsinnaasarput.</div>',
    auroraTitle3: 'Aqqusaarneq',
    auroraText3: 'Arsarnerit pillugit oqaluttuaq Seqinermit aallaaveqarpoq. Seqineq partikilinik aniatitsiuartuuvoq avataarsuarmi ingerlaartunik. Tamanna taaneqartarpoq seqernup anoraa. Seqernup anoraa magnetfeltimik seqinermit nassataqartoq avataarsuarmi aalajangersimasumik sammiveqarani igeriussaasarpoq, taamaalillunilu nunarsuaq aamma aqqusaartarlugu. Nunarsuaq magnetfelteqarpoq amerlanertigut seqernup anoraanut eqqornaveeqqutitut ersinngitsutut atuuttoq, taamaattumik seqernup anoraa nunarsuaq avaqqullugu aqqusaartarpoq avataarsuarmullu ingerlaqqilluni. Ilaannikkooriarlunili seqernup anoraata magnetfeltia nunarsuup magnetfeltianut ikkuttarpoq taamaalillutik pisut arleriiaat aallartittarput, Nunarsuullu magneteqarfiani partikkilit sukkatsikkiartortarlutik. Nunarsuup magneteqarfia Nunarsuup magnetfeltianit poorneqartutut ippoq. Partikilit ilaat magnetfeltip titarniusaa malillugu Nunarsuup silaannaata iluanut ingerlaartarput.',
    auroraTitle4: 'Makro-miit mikrokosmosimut',
    auroraText4: 'Makro-miit mikrokosmosimut. Partikilit Nunarsuup magneteqarfianeersut magnetfelti malillugu Nunarsuup kaaviffiata isuinut ingerlaartarput, molekylinillu Nunarsuup silaannaaniittunik aqqusaagaqartarlutik. Aqqusaarineq arsarnernik pilersitsisarpoq. Partikilit kvælstoffinut silaannaallu molekyliinut aporaangamik qalipaatinik qaamasunik qorsunnik, aappalaartunik tunguusanillu pilersitsisarput. Partikilillu amerlatillugit tamanna pigaangat arsarnerit qilammi takussaalersarput.',
    auroraTitle5: 'Suna tamaat allanngorarpoq',
    auroraText5: 'Arsarnerit qilammi titarneriinnaanngillat qorsuusut, aalaartuuppulli tamatigut allanngorartuullutik. Ilaannikkooriarluta qasingajattunik taamaallaat takusaqartarpugut, ilaannikkulli qilak tamaat qaammarlugu qitittutut ilillutik arlariinnik qalipaaserlutik takussaasarput. Arsarnerimmi assigiinngitsuupput, magneteqarfimmi silaannarmilu susoqarnersoq apeqqutaatillugu.',
    auroraTitle6: 'Nalorninartut akissutissarsinikuunngitsut',
    auroraText6: 'Arsarnerit nunarsuarmi malunnaataapput paasiuminaatsut. Naak arsarnerit pinngortarneri pillugit paasisimasaqaraluarluta suli nalusaqaqaagut, assersuutigalugu: Pisut sorliit arsarnerit ilusaannut sananeqaataannullu aalajangiisuusarpat? Sooq Arsarnerit nunarsuup affaani avannarlermiittut kujallermiittullu ilaatigut malunnaatilimmik assigiinneq ajorpat? Partikilit magneteqarfimmiit nunarsuup silaannaanut pigaangamik sukkatsikkiartortarnerat qanoq aallartittarpa? Apeqqutit tassa taakkuupput ilisimatuut akiniarsarisaat.',
    auroraTitle7: 'Oqaluttuatoqqat',
    auroraText7: 'Issittumi oqaluttuatoqaqarpoq arsarnerit suunerinik isummersortunik. Vikingit qilammi qaamasut valkyriet saattuaasaannut tunngatittarsimavaat, itsarnisarsiuullu saamit nunaanni angakkut qilaataannik, arsarnernik ittunik assiliartalinnik nassaarnikuusimapput. Kalaallit Nunaanni Inuit kulturianni arsarnerit tassaatinneqartarsimapput toqusut tarningi, aarrit niaquisa saarnginik arsaattut. Arsarneqartillugu sapiiserluni uinngiarsoraanni ersaattaakkaanniluunniit, toqusut tarninginit tigutittoqariataarsinnaavoq.',
    latestAuroraActivity: 'Arsarnerit kingullersut',
    auroraLevel: 'Arsarnerit angisoqaat',
    detectedAt: 'Nalunaaruteqarpoq',
    noRecentActivity: 'Pisoqartitaqanngilaq kingullerni',
    noActivityMessage: 'Sumiiffimmi arsarnerit paasissutissanit nalunaaruteqanngilaq. Pisoqartitaasaammik nalunaarutit uunaaverput.',
    auroraChancesLow: 'Arsarnerit takuneqassaput sumiiffinni.',
    auroraChancesMedium: 'Arsarnerit takuneqassappunga sumiiffinni.',
    auroraChancesHigh: 'Arsarnerit takuneqassallutik sumiiffinni!',
    auroraChancesVeryHigh: 'Arsarnerit takuneqassaaq sumiiffinni manna!',
    auroraForecast: 'Arsarnerit silarsuarnerpaaffiat',
    activityDetails: 'Pisoqarfik nassuiaatigut',
    activityDetectedNear: 'pisoqarneq nalunaarusimavoq',
    timeIndicator: '',
    levelQuiet: 'Nipanngitsoq',
    levelSome: 'Ilaat',
    levelHigh: 'Angisoq',
    levelVeryHigh: 'Angisorpaasoq',
    levelExtreme: 'Annertuusup',
    levelNoData: 'No data',
    locationPermission: 'Sumiiffik tilladeribvi',
    accessGranted: 'Tilladeribvi tunngaveqarpoq',
    accessDenied: 'Tilladeribvi killeqarpoq',
    notRequested: 'Tukisimasanngilaq',
    revokeAccess: 'Tilladeribvi eqqarsaatigit',
    grantAccess: 'Tilladeribvi tunngasigit',
    pushNotifications: 'Push nalunaarutit',
    enabledAndSubscribed: 'Atuutilersineqarpoq allassimasumillu',
    permissionGrantedNotSubscribed: 'Tilladeribvi tunngasimaneq, allassinneqanngila',
    permissionDenied: 'Tilladeribvi killesimaneq',
    disableNotifications: 'Nalunaarutit naalakkersinnaasigit',
    notificationLevel: 'Nalunaarutit angisoqaat',
    selectNotificationLevel: 'Arsarnerit nalunaarutinnut angisoqaat anigorlugit',
    levelUpdated: 'Nalunaaruteqart angisoqaa tikkiteqqisimaneq',
    levelUpdateError: 'Nalunaarutit angisoqaat tikkiteqarsinnaanngilaq. Kingullermi uunartoq.',
    notificationEnableError: 'Nalunaarutit atuutilersineqarsinnaanngilaq. Kingullermi uunartoq.',
    cache: 'Cache',
    auroraDataCache: 'Arsarnerit cache',
    stations: 'Oqaluttuarivit',
    cached: 'Cache-mi inerisimavaa',
    noCache: 'Cache-anngilaq',
    polarOval: 'Polaroval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Polar elektrojet',
    storageUsed: 'atorneqarput',
    loading: 'Akissaarpoq...',
    clearCache: 'Cache sanigit',
    showOvationModel: 'Ovation-modelli takutitsiigit',
    hideOvationModel: 'Ovation-modelli ijertarsinnaagit',
    close: 'Matugit',
    installApp: 'App-mi installitsigit',
    installMessage: 'App-mi angerlarsimaffimminut oquutaasigit sivisuit isikkuminerannut uppernarsaanerannutlu.',
    tapShare: 'Safari-mi tunngatitsisoq naajaagit.',
    selectAddToHomeScreen: '"Angerlarsimaffiminut ilassigit" toqqakkit.',
    locationAccessTitle: 'Sumiiffinni isikkumineq',
    locationAccessMessage: 'Sumiiffik nalunaaruk globemi sumiiffik takusissavit arsarneritlu nalunaarutigineqassarai.',
    allowLocation: 'Sumiiffik tillaatigit',
    notificationsTitle: 'Nalunaarutigineqartut',
    notificationsMessage: 'Arsarnerit sumiiffinni paasineqarpat nalunaarutigineqassaat arsarnerit tamaasa takunnaarannut.',
    enableNotificationsAction: 'Nalunaarutigineqartut atuutilersitsitigit',
    installOnIOS: 'iOS-mi installitsitigit',
    installIOSMessage: 'Safari-mi "Tunngatitsigit" naajaasigit, kingorna "Angerlarsimaffiminut ilaasivik" app installitsinissaannut.',
    subscriptionStatus: 'Abonnement nalunaarutigisassat',
    nearestStation: 'Station qanerpaa',
    stationDistance: 'Ungasik',
    withinRange: 'Nalunaarutilersarnerup aqqusaani',
    outsideRange: 'Nalunaarutilersarnerullu saniatigut',
    activeSubscription: 'Nalunaarutilersarnerit piqqusivit',
    stationSelection: 'Station-i toqqaniit',
    automaticMode: 'Automatisk (qanerni)',
    manualMode: 'Inuuvunga toqqaneq',
    automaticModeDescription: 'Agoq station-mut abonneerineq',
    loadingStations: 'Station-it ajorneqartut...',
    stationUpdateError: 'Station-i toqqaneqartut tikkiteqqisarsinnaanngilaq. Kingullermi uunartoq.',
    subscribedToStations: 'Station-inut abonneerinissaat:',
    station: 'station',
    noStationsSelected: 'Station-it toqqaneqanngilat',
    stationActivityLevel: 'Station-ip suliassartai',
    showAuroraOvals: 'Aurora-t takutitsiuk',
    about: 'Pillugu',
    locationNotAvailable: 'Sumiiffik naleqqussanngilaq',
    changeBrowserSettings: 'Browser-imi aaqqissuutissani allannguutit',
    automaticModeRequiresLocation: 'Automatisk-imi sumiiffik isikkorneqassaaq stationit qanersortaat paasisinnissaat',
  },
  fo: {
    nowcast: 'Nowcast',
    map: 'Kort',
    aurora: 'Um Norðlýsið',
    settings: 'Stillingar',
    welcome: 'Vælkomin til Nordlys',
    track: 'Fylg við norðlýsi og fá fráboðanir tá ið norðlýsi sæst í tínum øki.',
    subscribe: 'Tekna teg til fráboðanir',
    unsubscribe: 'Avtekna teg frá fráboðanum',
    notificationsBlocked: 'Fráboðanir blokeraðar',
    enableNotifications: 'Aktivera fráboðanir',
    getNotified: 'Fá fráboðan tá ið norðlýsi sæst í tínum øki',
    settingsTitle: 'Stillingar',
    language: 'Mál',
    selectLanguage: 'Vel mál',
    location: 'Staður',
    notifications: 'Fráboðanir',
    auroraTitle1: 'Leiðbeining',
    auroraText1: 'Bestu líkindi at síggja bjart norðlýsi eru: <ul><li>Tá himmalin er myrkur og klárur og uttan fullmána og býarljós,</li><li>Tá tú ert nærindis norður- og suðurpólkringarnar (sí eina myndlaða staðseting á kortinum),</li><li>Frá kvøldi til morgun, men serliga um midnáttartíð,</li><li>Heyst, vetur og vár (men serstakliga um javndøgur),</li><li>Tá nógv magnetiskt virksemi er (tú kanst velja at fáa boð um virksemi í \'stillingar\'),</li><li>Tá nógv sólarvirksemi er (umleið ellivtahvørt ár).</li></ul>',
    auroraTitle2: 'Alheimsfyribrigdi',
    auroraText2: '<div>Vit kenna norðlýsið, men visti tú, at á sunnaru hálvu finst eitt lýsi av sama slagi, sum nevnist suðurlýsið?</div><div>Norðlýsi og suðurlýsi, aurora borealis og aurora australis, undir einum nevnt póllýsi, koma fyri nærum samstundis hvør sínu megin jørðina. Økið, har ljósið sæst, er ósjónliga bundið saman við jarðarinnar magnetfeltslinjum. Norðlýsi og suðurlýsi síggjast oftast í einum breiðum belti meira enn tveytúsund kilometrar burtur frá pólunum.</div>',
    auroraTitle3: 'Møtið',
    auroraText3: '<div>Søgan um norðlýsi byrjar við sólini. Úr sólini streyma áhaldandi bitlar, sum ferðast gjøgnum rúmdina. Hetta nevnist sólvindur. Saman við sólvindinum fylgir eitt magnetfelt. Á ferð síni gjøgnum rúmdina møtir sólvindurin eisini jørðini.</div><div>Jørðin hevur eisini eitt magnetfelt, ið sum oftast virkar sum eitt ósjónligt skjøldur ímóti sólvindinum; tí fer hann framvið jørðini og víðari út í rúmdina.</div><div>Men summatíðir blandast magnetfeltið í sólvindinum saman við jarðarmagnetfeltið. Úrslitið er ein samansett røð av hendingum, ið seta ferð á teir løddu bitlarnar í magnethavinum (magnetosferuni) kring jørðina. Hetta jarðarinnar magnethav er alt rúmið, sum er fevnt av jarðarmagnetfeltinum. Nakrir av hesum bitlunum ferðast fram við magnetfeltslinjunum inn í lofthavið kring jørðina (atmosferuna) – og geva norðlýsi.</div>',
    auroraTitle4: 'Frá alheimi til erheim',
    auroraText4: 'Bitlarnir frá magnethavinum fylgja magnetfeltinum niður móti jørðini kring pólarnar, har teir renna saman við mýlum í lofthavinum. Her verður norðlýsi til. Tá bitlarnir stoyta saman við køvievnis- og súrevnismýl, tendrast grønar, reyðar, bláar ella violettar ljósglæmur. Tá hetta hendir við nógvum bitlum samstundis, so síggja vit norðlýsið við berum eygum.',
    auroraTitle5: 'Altíð í broyting',
    auroraText5: 'Póllýsi er ikki bara ein grøn linja á himmalinum, men eitt dynamiskt fyribrigdi, sum broytist alla tíðina. Onkuntíð síggja vit bara eitt veikt grátt skapilsi, og aðrar tíðir er allur himmalin upplýstur av alljósum dansandi bondum í fleiri litum. Tað eru nevnliga fleiri ymisk sløg av póllýsi, alt eftir hvat hendir í magnethavinum og lofthavinum.',
    auroraTitle6: 'Óloystar gátur',
    auroraText6: 'Póllýsið er eitt fjøltáttað fyribrigdi. Hóast vit lutvíst kunna greiða frá, hvussu norðlýsi og suðurlýsi verða til, so er nógv enn, sum vit ikki vita, so sum: Hvør tilgongd/mekanisma er mest umráðandi fyri formar og bygnaðir í póllýsinum? Hví eru norðlýsi og suðurlýsi onkuntíð rættuliga ólík hvørjum øðrum? Hví økist ferðin á bitlunum frá magnethavinum til lofthavið? Hetta eru nakrir av spurningunum, vísindafólk royna at finna svar til.',
    auroraTitle7: 'Gudalæran',
    auroraText7: 'Á arktisku leiðunum eru ymiskar frásøgnir um, hvat norðlýsið mundi vera. Víkingarnir knýttu tað reikandi ljósið á himlinum at brynjuni hjá valkyrjunum, og hjá sámafólkinum hava fornfrøðingar funnið shamantrummur við teknum, sum avmynda norðlýsið. Í grønlendsku Inuitmentanini verður norðlýsið sagt at vera deyðar sálir, sum spæla bólt við einum roysningaskalla. Um tú floytaði ella klappaði, meðan norðlýsið var til staðar, var vandi fyri at tær deyðu sálirnar tóku teg við.',
    latestAuroraActivity: 'Seinasta norðlýsvirksemi',
    auroraLevel: 'Norðlýsstig',
    detectedAt: 'Funnið',
    noRecentActivity: 'Eingin nýggj virksemi',
    noActivityMessage: 'Tað er í løtuni eingin norðlýsvirksemi funnin í tínum øki. Fráboðanir vísa her, tá virksemi verður funnin.',
    auroraChancesLow: 'Tað eru nakrar møguleikar fyri norðlýsi nær tær.',
    auroraChancesMedium: 'Tað eru góðir møguleikar fyri norðlýsi nær tær.',
    auroraChancesHigh: 'Tað eru framúr møguleikar fyri norðlýsi nær tær!',
    auroraChancesVeryHigh: 'Norðlýsi er sera víst at síggja nær tær núna!',
    auroraForecast: 'Norðlýsframskodning',
    activityDetails: 'Virksemisupplýsingar',
    activityDetectedNear: 'virksemi nær',
    timeIndicator: 'kl.',
    levelQuiet: 'Kyrr',
    levelSome: 'Nakrar',
    levelHigh: 'Høg',
    levelVeryHigh: 'Sera høg',
    levelExtreme: 'Ekstrem',
    levelNoData: 'Einki data',
    locationPermission: 'Staðsetanarloyvi',
    accessGranted: 'Atgongd givin',
    accessDenied: 'Atgongd noktað',
    notRequested: 'Ikki biðið um',
    revokeAccess: 'Taka atgongd aftur',
    grantAccess: 'Geva atgongd',
    pushNotifications: 'Push-fráboðanir',
    enabledAndSubscribed: 'Virkjað og tekið til',
    permissionGrantedNotSubscribed: 'Loyvi givið, ikki tekið til',
    permissionDenied: 'Loyvi noktað',
    disableNotifications: 'Slökkja fráboðanir',
    notificationLevel: 'Fráboðanarstig',
    selectNotificationLevel: 'Vel lágmarksstig fyri norðlýsfráboðanir',
    levelUpdated: 'Fráboðanarstig dagført',
    levelUpdateError: 'Kundi ikki dagføra fráboðanarstig. Royn aftur seinni.',
    notificationEnableError: 'Kundi ikki virkja fráboðanir. Royn aftur seinni.',
    cache: 'Cache',
    auroraDataCache: 'Norðlýs dáta cache',
    stations: 'Stöðvar',
    cached: 'Í cache',
    noCache: 'Eingin cache',
    polarOval: 'Pólarin oval',
    ovationAurora: 'NOAA Ovation',
    polarElectroJet: 'Pólarin elektrojet',
    storageUsed: 'brúkt',
    loading: 'Lesur...',
    clearCache: 'Reinsa cache',
    showOvationModel: 'Vís Ovation-fyrirmynd',
    hideOvationModel: 'Fjal Ovation-fyrirmynd',
    close: 'Lat aftur',
    installApp: 'Goym forrit',
    installMessage: 'Legg hetta forrit á tína heimaskerm fyri skjótari atgongd og betri royndslu.',
    tapShare: 'Trýst á Deil-knappin í Safari.',
    selectAddToHomeScreen: 'Vel "Legg á heimaskerm".',
    locationAccessTitle: 'Staðsetanatilgongd',
    locationAccessMessage: 'Loyv atgongd til tína staðseting fyri at síggja har tú ert á jørðini og fáa persónligar norðlýsfráboðanir.',
    allowLocation: 'Loyv staðseting',
    notificationsTitle: 'Fráboðanir',
    notificationsMessage: 'Fá fráboðan tá norðlýsvirksemi verður funnin í tínum øki so tú missir aldri norðlýsið.',
    enableNotificationsAction: 'Virkja fráboðanir',
    installOnIOS: 'Goym á iOS',
    installIOSMessage: 'Trýst Deil í Safari, síðani "Legg á heimaskerm" fyri at goyma hetta forrit.',
    subscriptionStatus: 'Abonnementstøða',
    nearestStation: 'Nærmasta støð',
    stationDistance: 'Avstøða',
    withinRange: 'Innan fráboðanarøki',
    outsideRange: 'Utanfyri fráboðanarøki',
    activeSubscription: 'Virkin push-fráboðan',
    stationSelection: 'Støðval',
    automaticMode: 'Sjálvvirkandi (nærmast)',
    manualMode: 'Handvirkur val',
    automaticModeDescription: 'Abonner sjálvvirkandi á nærmast støðina sambært tíni støðu',
    loadingStations: 'Layar støðir...',
    stationUpdateError: 'Kundi ikki dagføra støðval. Royn aftur seinni.',
    subscribedToStations: 'Abonnerar á støðir:',
    station: 'støð',
    noStationsSelected: 'Eingin støð vald',
    stationActivityLevel: 'Støðvirkni',
    showAuroraOvals: 'Vís norðlýsis ovalar',
    about: 'Um',
    locationNotAvailable: 'Staðseting ikki tøk',
    changeBrowserSettings: 'Broyt í kaga stillingar',
    automaticModeRequiresLocation: 'Sjálvvirkandi háttur krevur staðsetingaratgongd fyri at finna nærmstu støðir',
  },
};

function getCurrentLanguage(): SupportedLang {
  const stored = localStorage.getItem('language') as SupportedLang;
  if (stored) return stored;

  // Auto-detect from browser/system language
  const browserLang = navigator.language.split('-')[0]; // "en-US" -> "en"
  const supportedLanguages: SupportedLang[] = ['en', 'no', 'da', 'sv', 'kl', 'fo'];

  return supportedLanguages.includes(browserLang as SupportedLang)
    ? browserLang as SupportedLang
    : 'en';
}

export function t(key: string): string {
  const lang = getCurrentLanguage();
  return translations[lang][key] || key;
}

window.addEventListener('language-changed', () => {
  document.querySelectorAll('[data-localize]').forEach(el => {
    const key = el.getAttribute('data-localize');
    const lang = getCurrentLanguage();
    if (key && translations[lang][key]) {
      el.textContent = translations[lang][key];
    }
  });
});